import type { UserOfficeAffiliation } from "le-coffre-resources/dist/Notary";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { GUEST_FOLDERS_UID, isGuestFoldersAffiliation, resolvePreferredRealOfficeUid } from "@Front/Utils/preferredRealOfficeUidHelpers";

export type NotaryMenuOfficeIdentity = {
	menuOfficeUid: string;
	menuRoleInOffice: string | null | undefined;
	storeRawOfficeUid: string;
};

export function findAffiliationByOfficeUid(
	offices: UserOfficeAffiliation[],
	officeUid: string,
): UserOfficeAffiliation | undefined {
	return offices.find((o) => o.office_uid === officeUid || o.uid === officeUid);
}

export function roleFromAffiliationOrContext(
	aff: UserOfficeAffiliation | undefined,
	permissionContext: PermissionContextDto | null | undefined,
): string | null | undefined {
	return aff?.role_in_office ?? permissionContext?.officeRole;
}

export function tryRealActiveOfficeIdentity(
	activeOffice: UserOfficeAffiliation | null,
	storeRawOfficeUid: string,
): NotaryMenuOfficeIdentity | null {
	const rawUid = activeOffice?.office_uid ?? activeOffice?.uid ?? "";
	if (!rawUid || isGuestFoldersAffiliation({ office_uid: rawUid, uid: rawUid })) {
		return null;
	}
	return {
		menuOfficeUid: rawUid,
		menuRoleInOffice: activeOffice?.role_in_office,
		storeRawOfficeUid,
	};
}

export function firstResolvedMenuIdentity(steps: Array<() => NotaryMenuOfficeIdentity | null>): NotaryMenuOfficeIdentity | null {
	for (const step of steps) {
		const hit = step();
		if (hit) {
			return hit;
		}
	}
	return null;
}

export function tryIdentityForOfficeUid(
	officeUid: string | null | undefined,
	availableOffices: UserOfficeAffiliation[],
	permissionContext: PermissionContextDto | null | undefined,
	storeRawOfficeUid: string,
): NotaryMenuOfficeIdentity | null {
	if (!officeUid || officeUid === GUEST_FOLDERS_UID) {
		return null;
	}
	const aff = findAffiliationByOfficeUid(availableOffices, officeUid);
	return {
		menuOfficeUid: officeUid,
		menuRoleInOffice: roleFromAffiliationOrContext(aff, permissionContext),
		storeRawOfficeUid,
	};
}

export function tryIdentityFromPreferredRealOffices(params: {
	availableOffices: UserOfficeAffiliation[];
	lastRealOfficeUidBeforeGuest: string | null;
	permissionContext: PermissionContextDto | null | undefined;
	storeRawOfficeUid: string;
}): NotaryMenuOfficeIdentity | null {
	const realOffices = params.availableOffices.filter((a) => !isGuestFoldersAffiliation(a));
	const preferred = resolvePreferredRealOfficeUid(params.lastRealOfficeUidBeforeGuest, realOffices);
	if (!preferred) {
		return null;
	}
	return tryIdentityForOfficeUid(
		preferred,
		params.availableOffices,
		params.permissionContext,
		params.storeRawOfficeUid,
	);
}

export function tryActiveThenPrimaryOfficeIdentity(
	permissionContext: PermissionContextDto | null | undefined,
	availableOffices: UserOfficeAffiliation[],
	storeRawOfficeUid: string,
): NotaryMenuOfficeIdentity | null {
	const fromCtxActive = tryIdentityForOfficeUid(
		permissionContext?.activeOfficeUid,
		availableOffices,
		permissionContext,
		storeRawOfficeUid,
	);
	if (fromCtxActive) {
		return fromCtxActive;
	}
	return tryIdentityForOfficeUid(
		permissionContext?.primaryOfficeUid,
		availableOffices,
		permissionContext,
		storeRawOfficeUid,
	);
}
